import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MenuIcon } from '../../assets/svg/SVGComponents';
import { Container, MenuList, TriggerButton } from './styles';

export interface IPostAction {
  /** Texto do item. Também é o que o leitor de tela anuncia. */
  label: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Navega ao escolher. Use um ou outro, não os dois. */
  to?: string;
  onSelect?: () => void;
  /** Pinta o item como ação de risco e o encosta no fim da lista. */
  destructive?: boolean;
}

interface IPostActionsMenuProps {
  actions: IPostAction[];
  /** O que o gatilho anuncia; o ícone sozinho não diz de qual post é. */
  triggerLabel?: string;
}

/**
 * Menu de ações do post.
 *
 * Existe porque as ações do autor moravam soltas no meio das tags, onde
 * disputavam espaço com elas e sumiam no celular. Recolhidas atrás de um
 * gatilho, elas param de competir com o conteúdo e cabe adicionar novas sem
 * reabrir a discussão de layout.
 *
 * O estado de aberto é local e o clique-fora escuta o container inteiro,
 * gatilho incluído. Ouvir só a lista faria o clique no próprio gatilho contar
 * como "fora": ele fecharia no mousedown e reabriria no click, e o menu nunca
 * fecharia por ali.
 */
const PostActionsMenu: React.FC<IPostActionsMenuProps> = ({
  actions,
  triggerLabel = 'Ações do post',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const close = useCallback((returnFocus = false) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close(true);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close]);

  const handleSelect = useCallback(
    (action: IPostAction) => {
      close();
      if (action.onSelect) action.onSelect();
      if (action.to) navigate(action.to);
    },
    [close, navigate],
  );

  if (actions.length === 0) return null;

  return (
    <Container ref={containerRef}>
      <TriggerButton
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(previous => !previous)}
      >
        <MenuIcon
          width="1.25rem"
          stroke="#63646a"
          strokeWidth={2}
          fill="none"
        />
      </TriggerButton>

      <MenuList role="menu" $open={isOpen} hidden={!isOpen}>
        {actions.map(action => {
          const Icon = action.icon;

          return (
            <li key={action.label}>
              <button
                type="button"
                role="menuitem"
                className={action.destructive ? 'destructive' : undefined}
                onClick={() => handleSelect(action)}
              >
                {Icon && (
                  <Icon
                    width="1rem"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                )}
                {action.label}
              </button>
            </li>
          );
        })}
      </MenuList>
    </Container>
  );
};

export default PostActionsMenu;
